"use client";
import { cn } from "../../lib/utils.mjs";
import { Dialog, DialogContent } from "./dialog.mjs";
import * as React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Command as Command$1 } from "cmdk";
//#region src/components/ui/command.tsx
/**
* shadcn Command — thin wrapper around cmdk + Radix Dialog. Standard
* usage (Cmd-K-style switcher / palette):
*
*   <CommandDialog open={open} onOpenChange={setOpen}>
*     <CommandInput placeholder="Type to filter…" />
*     <CommandList>
*       <CommandEmpty>No results.</CommandEmpty>
*       <CommandGroup heading="Projects">
*         <CommandItem onSelect={…}>foo</CommandItem>
*       </CommandGroup>
*     </CommandList>
*   </CommandDialog>
*
* Spec 67 §4.2 — used at 6+ projects to give the operator a fast
* keyboard-driven switcher. Same primitive can be reused for other
* Cmd-K surfaces (skills, agents, recent threads).
*/
const Command = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(Command$1, {
	ref,
	className: cn("flex h-full w-full flex-col overflow-hidden rounded-panel bg-glass-menu text-popover-foreground", className),
	...props
}));
Command.displayName = Command$1.displayName;
/**
* Pre-styled Dialog + Command combo for keyboard-driven palettes.
* The `Esc` key dismisses (Radix Dialog default).
*/
function CommandDialog({ open, onOpenChange, label = "Command palette", children }) {
	return /* @__PURE__ */ jsx(Dialog, {
		open,
		onOpenChange,
		children: /* @__PURE__ */ jsx(DialogContent, {
			className: "max-w-lg gap-0 overflow-hidden p-0",
			"aria-label": label,
			children: /* @__PURE__ */ jsx(Command, {
				className: "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5",
				children
			})
		})
	});
}
const CommandInput = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsxs("div", {
	className: "flex items-center border-b border-border px-3",
	"cmdk-input-wrapper": "",
	children: [/* @__PURE__ */ jsx(FontAwesomeIcon, {
		icon: faMagnifyingGlass,
		className: "mr-2 h-4 w-4 shrink-0 text-muted-foreground"
	}), /* @__PURE__ */ jsx(Command$1.Input, {
		ref,
		className: cn("flex h-11 w-full rounded-inset bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50", className),
		...props
	})]
}));
CommandInput.displayName = Command$1.Input.displayName;
const CommandList = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(Command$1.List, {
	ref,
	className: cn("max-h-[300px] overflow-y-auto overflow-x-hidden py-1", className),
	...props
}));
CommandList.displayName = Command$1.List.displayName;
const CommandEmpty = React.forwardRef((props, ref) => /* @__PURE__ */ jsx(Command$1.Empty, {
	ref,
	className: "py-6 text-center text-sm text-muted-foreground",
	...props
}));
CommandEmpty.displayName = Command$1.Empty.displayName;
const CommandGroup = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(Command$1.Group, {
	ref,
	className: cn("overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground", className),
	...props
}));
CommandGroup.displayName = Command$1.Group.displayName;
const CommandSeparator = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(Command$1.Separator, {
	ref,
	className: cn("-mx-1 h-px bg-border", className),
	...props
}));
CommandSeparator.displayName = Command$1.Separator.displayName;
const CommandItem = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(Command$1.Item, {
	ref,
	className: cn("relative flex cursor-default select-none items-center rounded-inset px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground data-[disabled='true']:pointer-events-none data-[disabled='true']:opacity-50", className),
	...props
}));
CommandItem.displayName = Command$1.Item.displayName;
function CommandShortcut({ className, ...props }) {
	return /* @__PURE__ */ jsx("span", {
		className: cn("ml-auto text-xs tracking-widest text-muted-foreground", className),
		...props
	});
}
CommandShortcut.displayName = "CommandShortcut";
//#endregion
export { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut };
